/** Collapsed "⚙ creatives.generate_matrix · done" chips; click to inspect input/result JSON. */
import { Check, ChevronDown, CircleSlash, Cog, Copy, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/cn";
import { Spinner } from "../ui/Feedback";
import { copyText, prettyJson } from "./util";

export type ToolStatus = "running" | "done" | "error" | "interrupted";

export interface ToolCallView {
  id: string;
  name: string;
  input?: unknown;
  status: ToolStatus;
  result?: unknown;
  error?: string;
}

const STATUS_LABEL: Record<ToolStatus, string> = { running: "running", done: "done", error: "failed", interrupted: "no result" };

export function ToolChipGroup({ calls, className }: { calls: ToolCallView[]; className?: string }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!calls.length) return null;
  const sel = calls.find((c) => c.id === open);
  return (
    <div className={cn("my-2", className)}>
      <div className="flex flex-wrap gap-1.5">
        {calls.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setOpen((o) => (o === c.id ? null : c.id))}
            aria-expanded={open === c.id}
            className={cn(
              "inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border px-2.5 text-[12px] transition-colors",
              c.status === "error" ? "border-danger/50 bg-danger-soft/50 text-danger hover:bg-danger-soft" : "border-line bg-surface text-muted hover:border-line-strong hover:text-fg",
              open === c.id && "border-line-strong text-fg",
            )}
          >
            <span className="shrink-0">
              {c.status === "running" ? (
                <Spinner size={12} />
              ) : c.status === "error" ? (
                <X className="size-3" />
              ) : c.status === "interrupted" ? (
                <CircleSlash className="size-3" />
              ) : (
                <Cog className="size-3" />
              )}
            </span>
            <span className="truncate font-mono text-[11.5px]">{c.name}</span>
            <span className="shrink-0 text-subtle">·</span>
            <span className={cn("shrink-0", c.status === "done" && "text-success", c.status === "running" && "text-fg/80")}>
              {c.status === "done" ? (
                <span className="inline-flex items-center gap-0.5">
                  <Check className="size-3" strokeWidth={2.5} />
                  {STATUS_LABEL.done}
                </span>
              ) : (
                STATUS_LABEL[c.status]
              )}
            </span>
            <ChevronDown className={cn("size-3 shrink-0 transition-transform", open === c.id && "rotate-180")} />
          </button>
        ))}
      </div>
      {sel && (
        <div className="mt-2 animate-pop-in overflow-hidden rounded-xl border border-line bg-surface-2">
          <JsonPanel label="Input" value={sel.input ?? {}} />
          {sel.status === "running" ? (
            <div className="flex items-center gap-2 border-t border-line px-3.5 py-3 text-[12px] text-muted">
              <Spinner size={12} /> Running…
            </div>
          ) : sel.error ? (
            <JsonPanel label="Error" value={sel.error} danger />
          ) : sel.status === "interrupted" ? (
            <div className="border-t border-line px-3.5 py-3 text-[12px] text-muted">The run ended before this tool returned.</div>
          ) : (
            <JsonPanel label="Result" value={sel.result} />
          )}
        </div>
      )}
    </div>
  );
}

function JsonPanel({ label, value, danger }: { label: string; value: unknown; danger?: boolean }) {
  const text = prettyJson(value);
  const [copied, setCopied] = useState(false);
  return (
    <div className="border-line first:border-t-0 [&:not(:first-child)]:border-t">
      <div className="flex items-center justify-between px-3.5 pt-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">
        <span className={cn(danger && "text-danger")}>{label}</span>
        <button
          type="button"
          onClick={() =>
            void copyText(text).then((ok) => {
              if (!ok) return;
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1200);
            })
          }
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 normal-case tracking-normal text-muted hover:bg-hover hover:text-fg"
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className={cn("max-h-72 overflow-auto px-3.5 pb-3 pt-1.5 font-mono text-[11.5px] leading-relaxed scrollbar-thin", danger ? "text-danger" : "text-fg/85")}>{text}</pre>
    </div>
  );
}
